import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiCheckCircle } from "react-icons/fi";
import axiosInstance from "../axiosinstance";
import FirmRegistrationForm from "./FirmRegistrationForm";
import "./SubscriptionPlan.css";

const features = [
  "Manage Multiple Subsidiaries",
  "Client & Document Management",
  "Invoice & Payment Tracking",
  "Visitor Management",
  "Ready-made GST & Compliance Checklists",
  "Ready-to-download Deed Maker",
];

const GST_RATE = 0.18;

const SubscriptionPlan = () => {
  const navigate = useNavigate();
  const [planMeta, setPlanMeta] = useState({
    plan_id: "",
    plan_name: "Annual Access Plan",
    plan_code: "",
    amount: 0,
    display_amount: 0,
    currency: "INR",
    duration_days: 365,
    max_subsidiaries: 0,
  });
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState("");
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [flowMessage, setFlowMessage] = useState("");
  const [flowMessageType, setFlowMessageType] = useState("info");

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setPlansLoading(true);
        const response = await axiosInstance.get("/api/subscription-plans");
        const plans = Array.isArray(response.data) ? response.data : [];
        
        if (plans.length === 0) {
          setPlansError("No active plan available for registration.");
          return;
        }

        const selectedPlan = plans[0];
        setPlanMeta({
          plan_id: selectedPlan.plan_id || "",
          plan_name: selectedPlan.plan_name || "Annual Access Plan",
          plan_code: selectedPlan.plan_code || "",
          amount: Number(selectedPlan.amount || 0),
          display_amount: Number(selectedPlan.display_amount || 0),
          currency: selectedPlan.currency || "INR",
          duration_days: Number(selectedPlan.duration_days || 365),
          max_subsidiaries: Number(selectedPlan.max_subsidiaries || 0),
        });
        setPlansError("");
      } catch (error) {
        const message =
          error?.response?.data?.message || "Failed to load subscription plan.";
        setPlansError(message);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const priceBreakdown = useMemo(() => {
    const basePrice = Number(planMeta.display_amount || 0);
    const discount = 0;
    const taxableAmount = Math.max(basePrice - discount, 0);
    const gstAmount = Math.round(taxableAmount * GST_RATE);
    const totalPayable = taxableAmount + gstAmount;

    return {
      basePrice,
      discount,
      gstAmount,
      totalPayable,
    };
  }, [planMeta.display_amount]);

  const formatMoney = (value) => {
    const amount = Number.isFinite(value) ? value : 0;

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: planMeta.currency || "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const durationLabel =
    planMeta.duration_days === 365 ? "year" : `${planMeta.duration_days} days`;

  const handleMaybeLater = () => {
    navigate("/login");
  };

  const handleProceedToRegistration = () => {
    if (!planMeta.plan_id) {
      setFlowMessage("Unable to continue: plan id is unavailable.");
      setFlowMessageType("error");
      return;
    }

    setFlowMessage("");
    setShowRegistrationForm(true);
  };

  const handleContinueToPayment = (firmId) => {
    // Placeholder until payment gateway is integrated
    console.log("Continue to payment with firm_id:", firmId);
  };

  const handleRegistered = ({ status, firm_id, message }) => {
    setShowRegistrationForm(false);

    if (status === 200) {
      setFlowMessage(message || "Registration already started. Continue payment.");
      setFlowMessageType("info");
    } else {
      setFlowMessage(message || "Firm registered successfully.");
      setFlowMessageType("success");
    }

    if (firm_id) {
      handleContinueToPayment(firm_id);
    }
  };

  return (
    <div className="subscription-plan-page">
    {!showRegistrationForm &&  (<div className="subscription-plan-card">
        <div className="subscription-plan-header">
          <span className="subscription-plan-tag">ANNUAL PLAN</span>
          <h1>Upgrade Your Portal Access</h1>
          <p>
            Get yearly access to premium operational modules built for CA firms
            to manage clients, billing, records, and compliance in one place.
          </p>
        </div>

        <div className="subscription-plan-section">
          <h2>What's Included</h2>
          <div className="subscription-feature-grid">
            {features.map((feature) => (
              <div className="subscription-feature-item" key={feature}>
                <FiCheckCircle />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="subscription-price-card">
          <div>
            <p className="subscription-price-label">
              {(planMeta.plan_name || "Annual Access Plan").toUpperCase()}
            </p>
            <p className="subscription-original-price">₹6,000</p>
            <p className="subscription-price-value">
              {plansLoading ? "Loading..." : `${formatMoney(priceBreakdown.basePrice)} + GST / ${durationLabel}`}
            </p>
            <p className="subscription-price-note">
              One plan for full annual product access.
            </p>
          </div>
          <div className="subscription-price-icon">OA</div>
        </div>

        <div className="subscription-total-box">
          <div className="subscription-total-row">
            <span>Base Price</span>
            <span>{plansLoading ? "--" : formatMoney(priceBreakdown.basePrice)}</span>
          </div>
          {/* <div className="subscription-total-row">
            <span>Discount</span>
            <span>{plansLoading ? "--" : formatMoney(priceBreakdown.discount)}</span>
          </div> */}
          <div className="subscription-total-row">
            <span>GST (18%)</span>
            <span>{plansLoading ? "--" : formatMoney(priceBreakdown.gstAmount)}</span>
          </div>
          <div className="subscription-total-row total-payable">
            <span>Total Payable</span>
            <span>{plansLoading ? "--" : formatMoney(priceBreakdown.totalPayable)}</span>
          </div>
        </div>

        <div className="subscription-actions">
          {flowMessage && (
            <p className={`subscription-flow-message ${flowMessageType}`}>
              {flowMessage}
            </p>
          )}
          {plansError && <p className="subscription-flow-message error">{plansError}</p>}
          <button
            type="button"
            className="subscription-primary-btn"
            onClick={handleProceedToRegistration}
            disabled={plansLoading || !planMeta.plan_id}
          >
            {plansLoading
              ? "Loading Plan..."
              : `Complete Registration & Pay - ${formatMoney(priceBreakdown.totalPayable)}`}
          </button>
          <button
            type="button"
            className="subscription-secondary-btn"
            onClick={handleMaybeLater}
          >
            Maybe Later
          </button>
        </div>
      </div>)}

      {showRegistrationForm && (
        <FirmRegistrationForm
          planId={planMeta.plan_id}
          planName={planMeta.plan_name}
          planAmount={planMeta.amount}
          onCancel={() => setShowRegistrationForm(false)}
          onRegistered={handleRegistered}
        />
      )}
    </div>
  );
};

export default SubscriptionPlan;


